import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from 'assets/icons/newTrash.svg';
import { Avatar, NewConfirmDialog } from 'components';
import { formatDateTime } from 'features/notifications/components/Notification/utils';
import { useAuth } from 'hooks';
import { theme } from 'styles';
import { Comment, InViewItemRef } from 'types';
import { getFullName } from 'utils';

import { useDeleteComment } from './hooks';
import {
  CommentAuthorInfo,
  CommentHeaderW,
  CommentText,
  CommentW,
  DateTime,
  DeleteButtonW,
  OwnerLabel,
  UserName,
} from './styled';

interface CommentListItemProps {
  comment: Comment;
  postAuthor: string;
  lastItemRef?: InViewItemRef;
  inModal?: boolean;
}

export function CommentListItem({
  comment,
  postAuthor,
  lastItemRef,
  inModal,
}: CommentListItemProps): ReactElement {
  const { user, isManager } = useAuth();
  const { t } = useTranslation();
  const {
    isConfirmDeleteLoading,
    isDeleteModalOpen,
    handleCloseDeleteModal,
    handleDeleteComment,
    handleRemoveClick,
  } = useDeleteComment(comment);

  const { created_at, author, content } = comment;
  const commentDate = new Date(created_at);
  const isCommentOwner = user?.id === author.id;
  const isPostOwner = author?.id === postAuthor;
  const userName = getFullName({ firstName: author.first_name, lastName: author.last_name });

  return (
    <CommentW ref={lastItemRef} inModal={inModal}>
      <CommentHeaderW>
        <Avatar size="s" userId={author.id} filename={author.avatar?.small || ''} />
        <CommentAuthorInfo>
          <UserName>{userName}</UserName>
          <DateTime>{formatDateTime(commentDate)}</DateTime>
        </CommentAuthorInfo>
        {isPostOwner && <OwnerLabel>Post Owner</OwnerLabel>}
        {(isCommentOwner || isManager) && (
          <DeleteButtonW onClick={handleRemoveClick}>
            <DeleteIcon style={{ width: 24, height: 24, fill: theme.palette.primary.main }} />
          </DeleteButtonW>
        )}
      </CommentHeaderW>

      <CommentText dangerouslySetInnerHTML={{ __html: content }} />

      <NewConfirmDialog
        title={t('document.list.deleteComment.title')}
        subTitle={t('document.list.deleteComment.subtitle')}
        open={isDeleteModalOpen}
        onConfirm={handleDeleteComment}
        onClose={handleCloseDeleteModal}
        isLoading={isConfirmDeleteLoading}
        submitLabel={t('document.list.deleteComment.successButton')}
        cancelLabel={t('document.list.deleteComment.cancelButton')}
      />
    </CommentW>
  );
}
